import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';

import WarningIcon from '@mui/icons-material/Warning';
import { FormControl, FormHelperText, TextField } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { getTimeRemaining } from '../../../utils/dateUtils';
import { Registry } from '../../FormBuilderLibrary';
import { getElementIcon, stringToHTML } from '../utils';
import MUIDatePicker from './MUI_DatePicker';
import StyledWrapper from './style';

const MUIStartEndDate = forwardRef((props, ref) => {
	const { name, defaultValue, disabled, data, isFormBuilder } = props;
	const [entryError, setEntryError] = useState(props?.data?.error?.error);

	const [startDate, setStartDate] = useState(
		defaultValue?.[0] ? new Date(defaultValue?.[0]) : null
	);
	const [endDate, setEndDate] = useState(defaultValue?.[1] ? new Date(defaultValue?.[1]) : null);
	// Store string values for form submission
	const [startDateStr, setStartDateStr] = useState(defaultValue?.[0] || '');
	const [endDateStr, setEndDateStr] = useState(defaultValue?.[1] || '');
	const [totalTime, setTotalTime] = useState();
	const [timeRemaining, setTimeRemaining] = useState();
	const [warningIcon, setWarningIcon] = useState(false);
	const [refresh, setRefresh] = useState(false);

	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			get value() {
				return [startDateStr, endDateStr];
			},
			getValue: () => [startDateStr, endDateStr]
		}),
		[startDateStr, endDateStr]
	);

	const timeRemainingStyle = isFormBuilder
		? { width: '100%', marginTop: '0px' }
		: { width: '100%' };

	const calculateTimeRemaining = () => {
		if (startDate && endDate && startDate.toString() === endDate.toString()) {
			setTotalTime('0d 0h 0m');
		} else if ((startDate, endDate) && startDate < endDate) {
			const { timeRemaining } = getTimeRemaining(endDate, startDate);
			setTotalTime(timeRemaining);
		} else {
			setTotalTime(null);
		}
	};

	const calculateRemainingTime = () => {
		const currentDate = new Date();
		if (startDate && startDate >= currentDate) {
			setTimeRemaining('Not Started');
			setWarningIcon(false);
		} else if (endDate && currentDate <= endDate) {
			const { timeRemaining, days } = getTimeRemaining(endDate, currentDate);
			setTimeRemaining(timeRemaining);
			days <= 10 ? setWarningIcon(true) : setWarningIcon(false);
		} else {
			setTimeRemaining('0d 0h 0m');
			setWarningIcon(false);
		}
	};

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);

	useEffect(() => {
		calculateTimeRemaining();
		calculateRemainingTime();
	}, [refresh, startDate, endDate]);

	useEffect(() => {
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.offsetWidth);
			}
		}, 100);
	});

	useEffect(() => {
		setEntryError(props?.data?.error?.error);
	}, [props?.data?.error]);

	const handleChange = () => {
		setEntryError(null);
	};

	const handleDateChange = (newValue, dateType) => {
		let dateStr = '';

		if (newValue) {
			if (newValue instanceof Date && !isNaN(newValue.getTime())) {
				try {
					dateStr = newValue.toISOString().split('T')[0];
				} catch (error) {
					console.warn('Failed to convert Date to ISO string:', error);
				}
			} else if (newValue && newValue.isValid && typeof newValue.format === 'function') {
				try {
					dateStr = newValue.format('YYYY-MM-DD');
				} catch (error) {
					console.warn('Failed to format Dayjs object:', error);
				}
			}
		}

		if (dateType === 'start') {
			setStartDate(newValue);
			setStartDateStr(dateStr);
		} else {
			setEndDate(newValue);
			setEndDateStr(dateStr);
		}
		setRefresh(!refresh);
		handleChange();
	};

	return (
		<>
			<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }}>
				<span style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Tooltip
						arrow
						placement="top"
						title={isOverflowing ? `${props?.data?.label}` : ''}
						enterTouchDelay={0}
					>
						<StyledWrapper.CustomLabel disabled={disabled}>
							<StyledWrapper.LabelContents ref={labelRef}>
								{isFormBuilder && getElementIcon(props?.data?.key)}
								{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
							</StyledWrapper.LabelContents>
							{isFormBuilder && (
								<span>
									{' '}
									{props?.data?.unique_identifier
										? ` (ff-${props?.data?.unique_identifier})`
										: ` (tmp-${props?.data?.temp_order})`}
								</span>
							)}
							{props?.data?.required && <StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>}
						</StyledWrapper.CustomLabel>
					</Tooltip>

					<div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
						<div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
							<MUIDatePicker
								defaultValue={defaultValue?.[0]}
								disabled={disabled}
								required={props?.data?.required}
								name={`${name}-start`}
								data={{
									label: 'Start Date',
									key: props?.data.key,
									unique_identifier: props?.data?.unique_identifier,
									temp_order: props?.data?.temp_order,
									min_date: data?.min_date || data?.min_value,
									max_date: endDate || data?.max_date || data?.max_value,
									startEndDate: true
								}}
								sx={{ fontWeight: '100' }}
								onChange={(newValue) => handleDateChange(newValue, 'start')}
								isFormBuilder={isFormBuilder}
							/>
						</div>

						<div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
							<MUIDatePicker
								defaultValue={defaultValue?.[1]}
								disabled={disabled}
								required={props?.data?.required}
								name={`${name}-end`}
								data={{
									label: 'End Date',
									key: props?.data.key,
									unique_identifier: props?.data?.unique_identifier,
									temp_order: props?.data?.temp_order,
									min_date: startDate || data?.min_date || data?.min_value,
									max_date: data?.max_date || data?.max_value,
									startEndDate: true
								}}
								onChange={(newValue) => handleDateChange(newValue, 'end')}
								isFormBuilder={isFormBuilder}
							/>
						</div>
					</div>
					{!data?.custom_options?.includes('hideTimePeriod') && (
						<div style={{ display: 'flex', gap: '10px' }}>
							<div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
								<StyledWrapper.HorizontalInput>
									<StyledWrapper.HorizontalInputLabel disabled={disabled}>
										<StyledWrapper.HorizontalInputLabelContents>
											Total Time
										</StyledWrapper.HorizontalInputLabelContents>
									</StyledWrapper.HorizontalInputLabel>
									<FormControl sx={{ width: '100%', flex: '5' }} variant="outlined">
										<TextField
											id={`${name}-totalTime`}
											value={totalTime || '(not defined)'}
											sx={{ width: '100%', height: '56px' }}
											disabled
											className={isFormBuilder ? 'removeMarginTop noBorder' : ''}
											InputLabelProps={{ shrink: isFormBuilder || undefined }}
										/>
									</FormControl>
								</StyledWrapper.HorizontalInput>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
								<StyledWrapper.HorizontalInput>
									<StyledWrapper.HorizontalInputLabel disabled={disabled}>
										<StyledWrapper.HorizontalInputLabelContents>
											Remaining Time
										</StyledWrapper.HorizontalInputLabelContents>
									</StyledWrapper.HorizontalInputLabel>
									<FormControl sx={{ width: '100%', flex: '5' }} variant="outlined">
										<StyledWrapper.InputReadOnlyCustom
											type={'text'}
											disabled={true}
											style={timeRemainingStyle}
											inputComponent={() => {
												return (
													<>
														<TextField
															InputLabelProps={{ shrink: isFormBuilder || undefined }}
															id={`${name}-timeRemaining`}
															sx={{ width: '100%', height: '56px' }}
															value={timeRemaining || '(not defined)'}
															disabled
														/>
														{warningIcon && <WarningIcon sx={{ fill: '#ED6C02' }} />}
													</>
												);
											}}
											label={'Time Remaining'}
										/>
									</FormControl>
								</StyledWrapper.HorizontalInput>
							</div>
						</div>
					)}
				</span>
			</StyledWrapper.FormControlNoBorder>
			<StyledWrapper.FormHelperTextContainer>
				{entryError && (
					<FormHelperText error={entryError}>{` ${props.data.label} ${entryError}`}</FormHelperText>
				)}
				{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
			</StyledWrapper.FormHelperTextContainer>
		</>
	);
});

MUIStartEndDate.displayName = 'MUI_StartEndDate';
Registry.register('MUI_StartEndDate', MUIStartEndDate);

export default MUIStartEndDate;
